/* Extend the native multipart form; never submit a second independent cart request. */
if (!customElements.get('fiorpaw-matching-mug')) {
  class FiorpawMatchingMug extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.closest('form');
      this.section = this.closest('.shopify-section');
      if (!this.form || !this.section) return;
      this.config = JSON.parse(this.querySelector('[data-match-config]').textContent);
      this.toggle = this.querySelector('[data-match-toggle]');
      this.options = this.querySelector('[data-match-options]');
      this.colours = this.querySelector('[data-match-colours]');
      this.error = this.querySelector('[data-match-error]');
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.form.addEventListener('input', () => this.update(), opts);
      this.form.addEventListener('change', () => this.update(), opts);
      this.form.addEventListener('quantity-selector:update', () => this.update(), opts);
      document.addEventListener('variant:update', event => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId || !this.section.contains(event.target)) return;
        // Native product-form listeners also run during this event. Read their final state.
        queueMicrotask(() => { if (this.isConnected) this.update(); });
      }, opts);
      document.addEventListener('fp:cart-settled', () => this.update(), opts);
      this.toggle.disabled = !this.config.variants.some(v => v.available);
      this.hidden = false;
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    get selected() { return this.toggle?.checked === true; }
    get blocked() { return this.requestUncertain === true; }

    get extraVariant() {
      const id = this.querySelector('[data-match-variant]:checked')?.value;
      return this.config.variants.find(v => String(v.id) === id);
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency
      }).format(cents / 100);
    }

    discountedPrice(variant) {
      // Shopify rounds percentage discount amounts down to the nearest cent.
      return variant.price - Math.floor(variant.price * this.config.discountPercentage / 100);
    }

    update() {
      const primary = this.config.variants.find(v => String(v.id) === this.form.querySelector('[name="id"]')?.value);
      const extra = this.extraVariant;
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      for (const [selector, variant] of [['[data-match-primary-image]', primary], ['[data-match-extra-image]', extra]]) {
        const image = this.querySelector(selector);
        if (!variant?.image || !image) continue;
        if (image.getAttribute('src') !== variant.image) image.src = variant.image;
        image.alt = variant.imageAlt;
      }
      const pending = this.form.closest('product-form-component').hasAttribute('data-fp-cart-pending');
      this.toggle.disabled = pending || this.blocked || !this.config.variants.some(v => v.available);
      this.colours.disabled = !this.selected || pending || this.blocked;
      this.options.hidden = !this.selected;
      const story = this.querySelector('[data-match-story]');
      if (story) story.hidden = !this.selected;
      this.toggle.setAttribute('aria-expanded', String(this.selected));
      this.toggle.setAttribute('aria-controls', this.options.id);
      this.toggleAttribute('data-pending', pending);
      if (extra) {
        this.querySelector('[data-match-price]').textContent = '+ ' + this.money(this.discountedPrice(extra));
        this.querySelector('[data-match-original-price]').textContent = this.money(extra.price);
      }
      if (primary && extra) this.querySelector('[data-match-total]').textContent = this.money(primary.price * quantity + this.discountedPrice(extra));
      this.querySelector('[data-match-quantity]').textContent = this.config.messages.quantity.replace('[quantity]', String(quantity));
      for (const text of this.form.querySelectorAll('.add-to-cart-text__content > span > span')) {
        if (!this.originalText) this.originalText = text.textContent;
        if (primary?.available) text.textContent = this.selected ? this.config.messages.add : this.originalText;
      }
      if (!this.blocked) this.error.hidden = true;
    }

    validateForCart() {
      if (this.blocked) { this.markUncertain(); return false; }
      if (!this.selected) return true;
      if (!this.extraVariant?.available) {
        this.error.textContent = this.config.messages.choose;
        this.error.hidden = false;
        return false;
      }
      return true;
    }

    prepareRequest(original) {
      if (!this.selected) return null;
      const body = new FormData();
      const extra = this.extraVariant;
      const primaryQuantity = Number(original.get('quantity') || 1);
      for (const [key, value] of original) {
        if (key === 'id') {
          body.append('items[0][id]', value);
          body.append('items[1][id]', String(extra.id));
        } else if (key === 'quantity') {
          body.append('items[0][quantity]', value);
        } else if (/^properties\[.*\]$/.test(key)) {
          const property = key.slice(11, -1);
          body.append('items[0][properties][' + property + ']', value);
          body.append('items[1][properties][' + property + ']', property === 'Design style' ? extra.title : value);
        } else if (key === 'selling_plan') {
          body.append('items[0][selling_plan]', value);
        } else if (!key.startsWith('fp_matching_colour_')) {
          body.append(key, value);
        }
      }
      if (!original.has('quantity')) body.append('items[0][quantity]', String(primaryQuantity));
      body.append('items[1][quantity]', '1');
      return { body, itemCount: primaryQuantity + 1 };
    }

    markUncertain() {
      // Shopify can add available stock even on a 422. Never silently retry either mug.
      this.requestUncertain = true;
      this.error.textContent = this.config.messages.uncertain;
      this.error.hidden = false;
      this.querySelector('[data-match-cart]').hidden = false;
      this.update();
    }
  }
  customElements.define('fiorpaw-matching-mug', FiorpawMatchingMug);
}
